import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "~/components/base/Button";
import { Icon } from "~/components/base/Icon";
import { useT } from "~/hooks/useT";
import { useAuthStore } from "~/stores/authStore";

type Mode = "password" | "magic";

/**
 * Login screen — ported from the React reference `components/login.jsx`.
 *
 * Two modes share the same left column:
 *   - password: email + password → `authStore.login()` → `/dashboard`
 *   - magic:    email           → `authStore.loginMagic()` → success card
 *
 * The split-screen chrome (brand, language switcher, dark right panel) lives
 * in `<AuthLayout/>`; this component renders only the form column content.
 */
export function Login() {
  const { t } = useT();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const loginMagic = useAuthStore((s) => s.loginMagic);

  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("oleg@brewbros.ua");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const submitPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErr("");
    if (!email.includes("@")) {
      setErr(t("invalid_email"));
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch {
      setErr(t("invalid_email"));
    } finally {
      setLoading(false);
    }
  };

  const submitMagic = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErr("");
    if (!email.includes("@")) {
      setErr(t("invalid_email"));
      return;
    }
    setLoading(true);
    try {
      await loginMagic(email);
      setMagicSent(true);
    } catch {
      setErr(t("invalid_email"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ margin: "auto 0", maxWidth: 400, width: "100%" }}>
      <div className="eyebrow" style={{ marginBottom: 14 }}>
        {t("wholesale_portal")}
      </div>
      <h1 className="h1" style={{ fontSize: 48, marginBottom: 12 }}>
        {t("login_title")}
      </h1>
      <p
        style={{
          color: "var(--ink-3)",
          fontSize: 14,
          marginBottom: 28,
          lineHeight: 1.5,
        }}
      >
        {t("login_sub")}
      </p>

      {/* Mode switcher — segmented buttons using aria-pressed rather
          than role=tablist; we don't want the full tab pattern's
          arrow-key contract or tabpanel id wiring for two buttons. */}
      <div
        role="group"
        aria-label={t("sign_in")}
        style={{
          display: "inline-flex",
          background: "var(--surface-3)",
          borderRadius: 6,
          padding: 2,
          marginBottom: 20,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
        }}
      >
        {(["password", "magic"] as const).map((m) => {
          const active = m === mode;
          return (
            <button
              key={m}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setMode(m);
                setMagicSent(false);
                setErr("");
              }}
              style={{
                padding: "6px 12px",
                borderRadius: 4,
                background: active ? "var(--surface)" : "transparent",
                color: active ? "var(--ink)" : "var(--ink-3)",
                fontWeight: active ? 600 : 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                boxShadow: active ? "var(--shadow-xs)" : "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {m === "password" ? t("password") : t("magic")}
            </button>
          );
        })}
      </div>

      {mode === "password" && (
        <form
          onSubmit={(e) => {
            void submitPassword(e);
          }}
        >
          <div style={{ marginBottom: 14 }}>
            <label className="label" htmlFor="login-email">
              {t("email")}
            </label>
            <input
              id="login-email"
              className={`input input--lg ${err ? "input--err" : ""}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              type="email"
            />
          </div>

          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <label
                className="label"
                style={{ margin: 0 }}
                htmlFor="login-password"
              >
                {t("password")}
              </label>
              <button
                type="button"
                onClick={() => setMode("magic")}
                style={{
                  fontSize: 12,
                  color: "var(--ink-3)",
                  textDecoration: "underline",
                  textUnderlineOffset: 2,
                  background: "none",
                  border: 0,
                  padding: 0,
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                {t("forgot")}
              </button>
            </div>
            <div style={{ position: "relative" }}>
              <input
                id="login-password"
                type={showPw ? "text" : "password"}
                className="input input--lg"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                style={{
                  position: "absolute",
                  right: 10,
                  top: 13,
                  color: "var(--ink-4)",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
                aria-label={showPw ? t("hide_password") : t("show_password")}
              >
                <Icon name={showPw ? "eyeoff" : "eye"} size={16} />
              </button>
            </div>
          </div>

          {err && (
            <div className="help help--err" style={{ marginTop: 6 }}>
              {err}
            </div>
          )}

          <Button
            variant="blue"
            size="lg"
            block
            style={{ marginTop: 20 }}
            type="submit"
            disabled={loading}
          >
            {loading ? t("signing_in") : t("sign_in")}
          </Button>
        </form>
      )}

      {mode === "magic" && !magicSent && (
        <form
          onSubmit={(e) => {
            void submitMagic(e);
          }}
        >
          <div style={{ marginBottom: 14 }}>
            <label className="label" htmlFor="magic-email">
              {t("email")}
            </label>
            <input
              id="magic-email"
              className={`input input--lg ${err ? "input--err" : ""}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              type="email"
            />
          </div>

          {err && (
            <div className="help help--err" style={{ marginTop: 6 }}>
              {err}
            </div>
          )}

          <Button
            variant="blue"
            size="lg"
            block
            style={{ marginTop: 20 }}
            type="submit"
            disabled={loading}
            leftIcon={<Icon name="link" size={14} />}
          >
            {loading ? t("signing_in") : t("magic_link_send")}
          </Button>
        </form>
      )}

      {mode === "magic" && magicSent && (
        <div
          style={{
            padding: 20,
            border: "1px solid var(--line)",
            background: "var(--surface)",
            borderRadius: 8,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              width: 40,
              height: 40,
              borderRadius: 8,
              background: "var(--blue-50)",
              color: "var(--blue)",
              placeItems: "center",
              marginBottom: 12,
            }}
          >
            <Icon name="mail" size={18} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
            {t("magic_check_email")}
          </div>
          <div
            style={{
              color: "var(--ink-3)",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {`${t("magic_sent")} ${email}. ${t("magic_expire")}`}
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setMagicSent(false);
              setMode("password");
            }}
          >
            {t("back_to_password")}
          </Button>
        </div>
      )}

      <div
        style={{
          marginTop: 32,
          fontSize: 13,
          color: "var(--ink-3)",
        }}
      >
        {t("no_account")}{" "}
        <Link
          to="/register"
          style={{
            color: "var(--ink)",
            textDecoration: "underline",
            textUnderlineOffset: 2,
            fontWeight: 500,
          }}
        >
          {t("request")}
        </Link>
      </div>
    </div>
  );
}

export default Login;
